import {SortableContext, horizontalListSortingStrategy} from '@dnd-kit/sortable';
import {Show} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {
  GROUP_HEADER_ROW_HEIGHT,
  HEADER_CELL_BORDER_BOTTOM,
  THEAD_BORDER,
} from '../../constants/borderStyles';
import {EagColumn} from '../../types/EagColumn';
import {EagColumnState} from '../../types/EagColumnState';
import type {GroupRowModel} from '../../utils/groupRowModel';
import {ActionHeaderCell} from './components/ActionHeaderCell/ActionHeaderCell';
import {GroupHeaderRow} from './components/GroupHeaderRow/GroupHeaderRow';
import {HeaderCell} from './components/HeaderCell/HeaderCell';
import {SelectionHeaderCell} from './components/SelectionHeaderCell/SelectionHeaderCell';

// DndContext is intentionally absent here — it lives in EagGridTable (the parent),
// wrapping the outer <div> instead of <table>. This prevents dnd-kit accessibility
// <div>s from being injected as invalid children of <table>.
//
// Drag state (activeDragId, overColumnId, dropSide) is read directly from the
// EagGridStore by HeaderCell — no prop drilling through this layer.
//
// Column menu state (showColumnMenu, showPinning, showAutosize, filterState,
// and all callbacks) is provided via ColumnMenuContext by EagGridTable —
// no prop drilling through this layer either.

export type HeaderRowProps = {
  columns: EagColumn[];
  columnStateMap: Map<string, EagColumnState>;
  nonRightPinnedColumns: EagColumn[];
  rightPinnedColumns: EagColumn[];
  onSort: (key: string, additive: boolean) => void;
  onResizeStart: (colKey: string, startX: number, colIndex: number) => void;
  sortableIds: string[];
  pinnedLeftOffsets: Map<string, number>;
  pinnedRightOffsets: Map<string, number>;
  /** Live-order group row model — null when the group header row is not shown. */
  groupRowModel: GroupRowModel | null;
  // Pinning edge keys — used to compute isLastPinnedLeft / isFirstPinnedRight per cell
  lastPinnedLeftKey: string | null;
  firstPinnedRightKey: string | null;
  // Action column props — only present when action column is enabled
  actionColumnWidth?: number;
  headerHeightPx?: number;
  // Maps column key → <col> DOM index in colgroup. Accounts for filler position.
  colIndexMap: Map<string, number>;
  // Selection mode — controls whether to show SelectAllCheckbox or empty <th>
  rowSelectMode: 'SINGLE' | 'MULTIPLE' | 'NONE';
};

export function HeaderRow(props: HeaderRowProps) {
  const showGroupRow = props.groupRowModel != null;

  // When the group header row is visible, the main header row cells must use
  // top: GROUP_HEADER_ROW_HEIGHT so they stick directly below the group row
  // instead of overlapping it at top: 0.
  const stickyTop = showGroupRow ? GROUP_HEADER_ROW_HEIGHT : 0;

  const renderHeaderCell = (column: EagColumn, previousColumn?: EagColumn) => {
    const columnState = props.columnStateMap.get(column.key);
    if (!columnState) {
      return null;
    }
    return (
      <HeaderCell
        key={column.key}
        column={column}
        columnState={columnState}
        colIndex={props.colIndexMap.get(column.key) ?? 0}
        onSort={props.onSort}
        onResizeStart={props.onResizeStart}
        pinnedLeftOffset={props.pinnedLeftOffsets.get(column.key)}
        pinnedRightOffset={props.pinnedRightOffsets.get(column.key)}
        isLastPinnedLeft={props.lastPinnedLeftKey === column.key}
        isFirstPinnedRight={props.firstPinnedRightKey === column.key}
        stickyTop={stickyTop}
        previousColumn={previousColumn}
        previousColIndex={
          isNotNil(previousColumn) ? props.colIndexMap.get(previousColumn.key) : undefined
        }
      />
    );
  };

  return (
    <SortableContext items={props.sortableIds} strategy={horizontalListSortingStrategy}>
      <thead style={THEAD_BORDER}>
        <Show when={props.groupRowModel}>
          {(groupRowModel) => (
            <GroupHeaderRow
              model={groupRowModel}
              hasCheckboxColumn={props.rowSelectMode !== 'NONE'}
              actionColumnWidth={props.actionColumnWidth}
            />
          )}
        </Show>
        <tr>
          {/* checkbox pseudo-column header: SelectAllCheckbox for MULTIPLE, empty <th> for SINGLE */}
          <SelectionHeaderCell rowSelectMode={props.rowSelectMode} stickyTop={stickyTop} />
          {/* left-pinned + unpinned header cells — each cell also receives the previous
              column so it can render the companion left-edge resize handle for it */}
          {props.nonRightPinnedColumns.map((column, index) =>
            renderHeaderCell(column, props.nonRightPinnedColumns[index - 1])
          )}
          {/* filler th: pairs with filler <col /> to absorb remaining table width.
              Positioned between unpinned and right-pinned so pinned columns sit at the right edge. */}
          <th
            aria-hidden
            style={{
              position: 'sticky',
              top: stickyTop,
              background: 'white',
              ...HEADER_CELL_BORDER_BOTTOM,
            }}
          />
          {/* right-pinned header cells — no previous column: their own handle already
              sits on the left edge, so a companion would overlap it */}
          {props.rightPinnedColumns.map((column) => renderHeaderCell(column))}
          {/* action header cell — always at the far right */}
          <Show when={props.actionColumnWidth ? props.actionColumnWidth : null}>
            {(actionColumnWidth) => (
              <ActionHeaderCell
                actionColumnWidth={actionColumnWidth}
                heightPx={props.headerHeightPx ?? 40}
                stickyTop={stickyTop}
              />
            )}
          </Show>
        </tr>
      </thead>
    </SortableContext>
  );
}
